import { hashDiagnosticEntity } from '../../tx-engine/diagnostics/lifecycle.js';
import { emit } from '../events.js';

const PARTICIPANT_FIELD_BY_ROLE = {
  trader: 'trader',
  backer: 'backer',
  booster: 'booster',
} as const;

const VIEWER_ONLY_FIELDS = new Set([
  'canClaim',
  'earnedD6',
  'rewardScope',
  'userShareD6',
  'viewerBalanceD6',
  'viewerClaimableD6',
]);

export interface ParticipationEntityDiagnostic {
  readonly kind:
    | 'participant-key-mismatch'
    | 'stale-relation-event-rejection'
    | 'rank-index-recovery-failure'
    | 'public-private-field-boundary';
  readonly entityHash: string;
  readonly source?: string;
}

/** Reject a malformed public relation before it reaches the shared cache. */
export function canMaterializeParticipationEntity(
  row: Readonly<Record<string, unknown>>,
  participationId: string,
): boolean {
  const [role, market, walletAddress, ...rest] = participationId.split(':');
  const validOutcomeScope =
    role === 'trader' &&
    rest.length === 2 &&
    rest[0] === 'outcome' &&
    /^(?:0|[1-9]\d*)$/.test(rest[1] ?? '');
  if (
    (rest.length > 0 && !validOutcomeScope) ||
    market === undefined ||
    market.length === 0 ||
    walletAddress === undefined ||
    walletAddress.length === 0 ||
    role === undefined ||
    !(role in PARTICIPANT_FIELD_BY_ROLE)
  ) {
    recordParticipationEntityDiagnostic({
      kind: 'participant-key-mismatch',
      entityHash: hashDiagnosticEntity(participationId),
      source: 'malformed-id',
    });
    return false;
  }

  if (Object.keys(row).some((field) => VIEWER_ONLY_FIELDS.has(field))) {
    recordParticipationEntityDiagnostic({
      kind: 'public-private-field-boundary',
      entityHash: hashDiagnosticEntity(participationId),
      source: 'public-relation',
    });
    return false;
  }

  const participantWallet = participationWallet(
    row,
    role as keyof typeof PARTICIPANT_FIELD_BY_ROLE,
  );
  if (participantWallet !== walletAddress) {
    recordParticipationEntityDiagnostic({
      kind: 'participant-key-mismatch',
      entityHash: hashDiagnosticEntity(participationId),
      source: 'participant-wallet',
    });
    return false;
  }
  return true;
}

function participationWallet(
  row: Readonly<Record<string, unknown>>,
  role: keyof typeof PARTICIPANT_FIELD_BY_ROLE,
): unknown {
  if (role === 'trader' && typeof row.walletAddress === 'string') return row.walletAddress;
  const participant = row[PARTICIPANT_FIELD_BY_ROLE[role]];
  return participant !== null && typeof participant === 'object'
    ? (participant as Record<string, unknown>).walletAddress
    : undefined;
}

export function recordParticipationEntityDiagnostic(
  diagnostic: ParticipationEntityDiagnostic,
): void {
  emit('participation-entity:diagnostic', diagnostic);
}
